import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { loadSwapReferenceData } from "./helper/tradeFromScratchHelper";
import tradeFromScratchStore from "@/store/tradeFromScratchStore";
import { sendSwaptionTicket } from "@/store/tradeFromScratchStoreACTIONS";
import {
  returnHighlightedKeys,
  useTicketShell,
} from "./helper/useTicketShell";
import SwaptionCoreFields from "./swaption/SwaptionCoreFields";
import SwaptionDetailFields from "./swaption/SwaptionDetailFields";
import { useSwaptionTicket } from "./swaption/useSwaptionTicket";
import TicketStatusLine from "./ticketFields/TicketStatusLine";
import TicketHeader from "./ticketFields/TicketHeader";

// LQC-2677 - the Leg Creation swaption ticket: option terms over a swap underlying,
// reusing the swap leg rows and conventions.

interface ISwaptionTicketProps {
  ticketId: string;
}

const SwaptionTicket = ({ ticketId }: ISwaptionTicketProps) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const shell = useTicketShell(ticketId);
  const { missingEntries, dateIssue } = useSwaptionTicket(ticketId);
  const ensureTicket = tradeFromScratchStore(
    (state) => state.ensureSwaptionTicket,
  );
  const resetTicket = tradeFromScratchStore(
    (state) => state.resetSwaptionTicket,
  );

  useEffect(() => {
    ensureTicket(ticketId);
  }, [ticketId, ensureTicket]);

  useEffect(() => {
    loadSwapReferenceData(getAccessTokenSilently);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSend =
    missingEntries.length === 0 && !dateIssue && !shell.isSending;
  const highlightedKeys = returnHighlightedKeys(
    shell.isHoveringSend,
    canSend,
    missingEntries,
  );

  const handleSend = async () => {
    const sent = await sendSwaptionTicket({
      ticketId,
      user,
      getAccessTokenSilently,
    });
    if (sent) shell.markSent();
  };

  return (
    <div
      data-testid="SwaptionTicketContainer"
      id="SwaptionTicketContainer"
      className="flex h-full w-full flex-col gap-2"
    >
      <TicketHeader
        testIdPrefix="SwaptionTicket"
        instrument={shell.instrument}
        onInstrumentChange={shell.setInstrument}
        canSend={canSend}
        isSending={shell.isSending}
        isConfirming={shell.isConfirmingSend}
        onSend={handleSend}
        onClear={() => resetTicket(ticketId)}
        onSendHoverChange={shell.setIsHoveringSend}
      />
      <TicketStatusLine
        testIdPrefix="SwaptionTicket"
        lastSent={shell.lastSent}
        missingFields={missingEntries.map((entry) => entry.label)}
        hasDateIssue={!!dateIssue}
        isBlocking={shell.isHoveringSend && !canSend}
      />
      <SwaptionCoreFields
        ticketId={ticketId}
        highlightedKeys={highlightedKeys}
      />
      <SwaptionDetailFields
        ticketId={ticketId}
        highlightedKeys={highlightedKeys}
      />
    </div>
  );
};

export default SwaptionTicket;
